import { NuxtRoutes } from '~/constants/routes'

export const DOCUMENT_TITLE_BRAND = 'qutee.ai'

export const DOCUMENT_TITLE_I18N_KEY_BY_ROUTE_NAME: Record<string, string> = {
  [NuxtRoutes.Agenten]: 'navigation.agents',
  [NuxtRoutes.AgentIntentView]: 'navigation.agents',
  [NuxtRoutes.AgentPersoenlichkeitView]: 'navigation.agents',
  [NuxtRoutes.AgentPersoenlichkeitChange]: 'navigation.agents',
  [NuxtRoutes.AgentVerfuegbarkeitView]: 'navigation.agents',
  [NuxtRoutes.Anrufe]: 'navigation.calls',
  [NuxtRoutes.Home]: 'navigation.overview',
  [NuxtRoutes.Dashboard]: 'navigation.overview',
  [NuxtRoutes.DeineAnfragen]: 'documentTitle.yourRequests',
  [NuxtRoutes.Unsubscribe]: 'documentTitle.unsubscribe',
  [NuxtRoutes.Kampagnen]: 'navigation.campaigns',
  [NuxtRoutes.KampagnenErstellen]: 'campaigns.wizard.title',
  [NuxtRoutes.KampagneDetail]: 'navigation.campaigns',
  [NuxtRoutes.AdminBenutzer]: 'navigation.users',
  [NuxtRoutes.AdminKundenbereich]: 'navigation.customerArea',
  [NuxtRoutes.AuthLogin]: 'auth.login.signIn',
  [NuxtRoutes.AuthSignup]: 'auth.signup.register',
  [NuxtRoutes.AuthMfaChallenge]: 'auth.mfa.challengeTitle',
  [NuxtRoutes.AuthMfaSetup]: 'auth.mfa.setupTitle',
  [NuxtRoutes.AuthForgotPassword]: 'auth.forgotPassword.title',
  [NuxtRoutes.AuthForgotPasswordAction]: 'auth.forgotPasswordAction.title',
  [NuxtRoutes.SettingsAbrechnung]: 'navigation.mySubscription',
  [NuxtRoutes.SettingsMeineDaten]: 'navigation.myData',
  [NuxtRoutes.SettingsMitarbeiterVerwalten]: 'mitarbeiterVerwalten.pageTitle',
  [NuxtRoutes.SettingsRechtlicheAngaben]: 'documentTitle.legalNotice',
  [NuxtRoutes.SettingsTagsVerwalten]: 'navigation.myTags',
  [NuxtRoutes.SettingsCrmFiles]: 'navigation.myCrmFiles',
  [NuxtRoutes.SettingsCustomerSettings]: 'navigation.customerSettings',
  [NuxtRoutes.SettingsEmailTemplates]: 'navigation.emailTemplates',
  [NuxtRoutes.SettingsPartnerSettings]: 'navigation.partnerSettings',
  [NuxtRoutes.SettingsTelefonie]: 'navigation.telephony',
  [NuxtRoutes.SettingsIntegrationen]: 'navigation.integrations',
  [NuxtRoutes.SettingsIntegrationenVerbindungen]: 'navigation.integrations',
  [NuxtRoutes.SettingsIntegrationenVerbindungenHinzufuegen]: 'navigation.integrations',
  [NuxtRoutes.SettingsIntegrationenModule]: 'navigation.integrations',
  [NuxtRoutes.SettingsIntegrationenModuleKalender]: 'navigation.integrations',
}

/** Longer prefixes first so `/kampagnen/neu` wins over `/kampagnen`. */
export const DOCUMENT_TITLE_I18N_KEY_BY_PATH_PREFIX: Array<[string, string]> = [
  ['/settings/integrationen', 'navigation.integrations'],
  ['/settings/partnereinstellungen', 'navigation.partnerSettings'],
  ['/settings/mitarbeiter-verwalten', 'mitarbeiterVerwalten.pageTitle'],
  ['/settings/email-templates', 'navigation.emailTemplates'],
  ['/settings/customer-settings', 'navigation.customerSettings'],
  ['/settings/rechtliche-angaben', 'documentTitle.legalNotice'],
  ['/settings/tags-verwalten', 'navigation.myTags'],
  ['/settings/crm-files', 'navigation.myCrmFiles'],
  ['/settings/meine-daten', 'navigation.myData'],
  ['/settings/abrechnung', 'navigation.mySubscription'],
  ['/settings/telefonie', 'navigation.telephony'],
  ['/admin/kundenbereich', 'navigation.customerArea'],
  ['/admin/benutzer', 'navigation.users'],
  ['/kampagnen/neu', 'campaigns.wizard.title'],
  ['/kampagnen', 'navigation.campaigns'],
  ['/agenten', 'navigation.agents'],
  ['/anrufe', 'navigation.calls'],
  ['/deine-anfragen', 'documentTitle.yourRequests'],
  ['/unsubscribe', 'documentTitle.unsubscribe'],
  ['/auth/forgotPasswordAction', 'auth.forgotPasswordAction.title'],
  ['/auth/forgotPassword', 'auth.forgotPassword.title'],
  ['/auth/mfa-challenge', 'auth.mfa.challengeTitle'],
  ['/auth/mfa-setup', 'auth.mfa.setupTitle'],
  ['/auth/signup', 'auth.signup.register'],
  ['/auth/login', 'auth.login.signIn'],
  ['/home', 'navigation.overview'],
]

export function normalizeDocumentTitlePath(path: string): string {
  const withoutQuery = path.split('?')[0] ?? path
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1)
  }
  return withoutQuery
}

export function buildDocumentTitle(parts: Array<string | null | undefined>): string {
  const cleaned = parts
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part))
  return cleaned.join(' · ') || DOCUMENT_TITLE_BRAND
}

export function formatAgentDisplayName(agent: {
  name?: string | null
  internalLabel?: string | null
}): string {
  const name = agent.name?.trim() ?? ''
  const internalLabel = agent.internalLabel?.trim() ?? ''
  if (name && internalLabel) return `${name} (${internalLabel})`
  return name || internalLabel
}

export function resolveDocumentTitleI18nKey(
  routeName: unknown,
  path: string,
  matchedNames: unknown[] = [],
): string | null {
  const names = [routeName, ...matchedNames]
    .map(name => (typeof name === 'string' ? name : ''))
    .filter(Boolean)

  for (const name of names) {
    const fromName = DOCUMENT_TITLE_I18N_KEY_BY_ROUTE_NAME[name]
    if (fromName) return fromName
  }

  const normalized = normalizeDocumentTitlePath(path)
  for (const [prefix, key] of DOCUMENT_TITLE_I18N_KEY_BY_PATH_PREFIX) {
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
      return key
    }
  }

  return null
}

export function firstRouteParam(value: unknown): string | null {
  if (Array.isArray(value)) {
    const first = value[0]
    return typeof first === 'string' && first ? first : null
  }
  return typeof value === 'string' && value ? value : null
}
